import { useState } from 'react';
import { Link } from 'react-router-dom';
import { PROJEKT_WAEHRUNG, betrag, centAlsEingabe, centAusText, type BudgetStand, type KategorieAnsicht } from '@beholder/shared/module/budget';
import { api } from '../../core/api';
import { Symbol } from '../../core/symbole';
import { Auswahl, Dialog, Eingabe, Feld, Karte, Knopf, Leer, Meldung, useAktion } from '../../core/ui';
import { PlanLeiste, WarnMarke } from './Diagramme';
import { pfad } from './hilfe';
import { Rechner } from './Rechner';

export function Uebersicht({
  projektId,
  s,
  darfSchreiben,
  darfLoeschen,
  neuLaden,
}: {
  projektId: string;
  s: BudgetStand;
  darfSchreiben: boolean;
  /** Kategorien löschen nur die Projektleitung. */
  darfLoeschen: boolean;
  neuLaden(): void;
}) {
  const [einst, setEinst] = useState(false);
  const [kat, setKat] = useState<{ k: KategorieAnsicht | null; eltern: string | null } | null>(null);
  const w = PROJEKT_WAEHRUNG;
  const g = s.gesamt;
  const zuKlappen = useZuklappen();

  return (
    <div className="bu-raster-2">
      <div className="stapel">
        <Karte
          titel="Gesamtbudget"
          aktion={darfSchreiben && <Knopf klein onClick={() => setEinst(true)}>{Symbol.einstellungen} Einstellungen</Knopf>}
        >
          <div className="bu-kennzahlen">
            <Kennzahl titel="Budget" wert={g.planCent === null ? 'nicht festgelegt' : betrag(g.planCent, w)} hinweis={s.einstellungen.gesamtCent === null && g.planCent !== null ? 'Summe der Kategorien' : undefined} />
            <Kennzahl
              titel="Ausgegeben"
              wert={betrag(g.istCent, w)}
              hinweis={`${betrag(g.bezahltCent, w)} bezahlt · ${betrag(g.offenCent, w)} offen${g.istNettoCent !== g.istCent ? ` · netto ${betrag(g.istNettoCent, w)}` : ''}`}
            />
            <Kennzahl
              titel="Rest"
              wert={g.restCent === null ? '–' : betrag(g.restCent, w)}
              art={g.restCent !== null && g.restCent < 0 ? 'gefahr' : undefined}
              hinweis={g.prozent !== null ? `${g.prozent} % verbraucht` : undefined}
            />
            <Kennzahl titel="Offene Angebote" wert={betrag(g.angeboteCent, w)} hinweis="mögliche Kosten, noch keine Ausgabe" />
          </div>
          <PlanLeiste gross planCent={g.planCent} istCent={g.istCent} angeboteCent={g.angeboteCent} warnung={g.warnung} waehrung={w} />
          <div className="bu-legende-zeile leise klein">
            <span><i className="bu-punkt bu-punkt-ist" /> ausgegeben</span>
            <span><i className="bu-punkt bu-punkt-angebot" /> offene Angebote</span>
            <span><i className="bu-punkt bu-punkt-plan" /> Budget</span>
          </div>
          <div className="reihe bu-hinweise">
            <WarnMarke warnung={g.warnung} />
            {g.nichtVerteiltCent !== null && g.nichtVerteiltCent > 0 && (
              <span className="bu-marke">{betrag(g.nichtVerteiltCent, w)} noch nicht auf Kategorien verteilt</span>
            )}
            {g.nichtVerteiltCent !== null && g.nichtVerteiltCent < 0 && (
              <span className="bu-marke bu-marke-warn">Kategorien verplanen {betrag(-g.nichtVerteiltCent, w)} mehr als das Gesamtbudget</span>
            )}
            {g.ohneKategorieCent > 0 && <span className="bu-marke">{betrag(g.ohneKategorieCent, w)} ohne Kategorie</span>}
            {g.ohneKursAnzahl > 0 && (
              <Link to={`/p/${projektId}/budget/belege`} className="bu-marke bu-marke-warn">
                {g.ohneKursAnzahl === 1 ? '1 Beleg' : `${g.ohneKursAnzahl} Belege`} ohne Umrechnungskurs – zählen noch nicht mit
              </Link>
            )}
            {s.vorschlaege.length > 0 && (
              <Link to={`/p/${projektId}/budget/vorschlaege`} className="bu-marke bu-marke-akzent">
                {s.vorschlaege.length === 1 ? '1 Vorschlag wartet' : `${s.vorschlaege.length} Vorschläge warten`} auf Bestätigung
              </Link>
            )}
          </div>
        </Karte>

        <Karte titel="Einnahmen und Saldo">
          <div className="bu-kennzahlen bu-kennzahlen-3">
            <Kennzahl
              titel="Einnahmen"
              wert={betrag(g.einnahmen.gesamtCent, w)}
              hinweis={g.einnahmen.anzahl ? `${betrag(g.einnahmen.erhaltenCent, w)} erhalten · ${betrag(g.einnahmen.erwartetCent, w)} erwartet` : 'Zuschüsse, Sponsoring, Verkäufe …'}
            />
            <Kennzahl titel="Ausgaben" wert={betrag(g.istCent, w)} hinweis="Rechnungen, bezahlt und offen" />
            <Kennzahl
              titel="Saldo"
              wert={`${g.saldoCent > 0 ? '+' : ''}${betrag(g.saldoCent, w)}`}
              art={g.saldoCent < 0 ? 'gefahr' : g.saldoCent > 0 ? 'ok' : undefined}
              hinweis={`Einnahmen − Ausgaben · tatsächlich geflossen ${betrag(g.saldoGeflossenCent, w)}`}
            />
          </div>
          {g.einnahmen.anzahl === 0 && (
            <p className="leise klein bu-ohne-abstand">
              Noch keine Einnahmen erfasst. <Link to={`/p/${projektId}/budget/belege?art=einnahme`}>Zu den Einnahmen</Link>
            </p>
          )}
        </Karte>

        <Karte
          titel="Kategorien"
          aktion={darfSchreiben && <Knopf klein art="haupt" onClick={() => setKat({ k: null, eltern: null })}>{Symbol.plus} Kategorie</Knopf>}
        >
          {s.kategorien.length === 0 ? (
            <Leer titel="Noch keine Kategorien">
              {darfSchreiben ? 'Lege Kategorien wie „Technik“ oder „Reise“ an und verteile das Budget darauf. Unterkategorien sind beliebig tief möglich.' : 'Hier stehen später die Kategorien mit ihrem Budget.'}
            </Leer>
          ) : (
            <ul className="bu-baum">
              {s.kategorien
                .filter((k) => !versteckt(k, s.kategorien, zuKlappen.zu))
                .map((k) => {
                  const hatKinder = s.kategorien.some((x) => x.elternId === k.id);
                  return (
                    <li key={k.id} className={`bu-baum-zeile bu-tiefe-${Math.min(k.tiefe, 5)}`} style={{ paddingLeft: `${k.tiefe * 18}px` }}>
                      <div className="bu-baum-kopf">
                        {hatKinder ? (
                          <button type="button" className="bu-klapp" aria-label={zuKlappen.zu.has(k.id) ? 'Aufklappen' : 'Zuklappen'} onClick={() => zuKlappen.umschalten(k.id)}>
                            {zuKlappen.zu.has(k.id) ? '▸' : '▾'}
                          </button>
                        ) : (
                          <span className="bu-klapp-leer" />
                        )}
                        <Link to={`/p/${projektId}/budget/belege?art=alle&kategorie=${k.id}`} className="bu-baum-name" title="Belege dieser Kategorie ansehen">
                          {k.name}
                        </Link>
                        <WarnMarke warnung={k.warnung} ueberverteilt={k.ueberverteilt} />
                        <span className="abstand" />
                        <span className="bu-baum-zahlen">
                          <strong>{betrag(k.istCent, w)}</strong>
                          <span className="leise"> / {k.planCent === null ? 'ohne Budget' : betrag(k.planCent, w)}</span>
                        </span>
                        {darfSchreiben && (
                          <span className="bu-baum-knoepfe">
                            <Knopf klein art="leise" title="Unterkategorie anlegen" aria-label={`Unterkategorie in ${k.name} anlegen`} onClick={() => setKat({ k: null, eltern: k.id })}>
                              {Symbol.plus}
                            </Knopf>
                            <Knopf klein art="leise" title="Bearbeiten" aria-label={`${k.name} bearbeiten`} onClick={() => setKat({ k, eltern: k.elternId })}>
                              ✎
                            </Knopf>
                          </span>
                        )}
                      </div>
                      <PlanLeiste planCent={k.planCent} istCent={k.istCent} angeboteCent={k.angeboteCent} warnung={k.warnung} waehrung={w} />
                      <div className="bu-baum-fuss leise klein">
                        {k.restCent !== null && (
                          <span className={k.restCent < 0 ? 'bu-text-gefahr' : ''}>
                            {k.restCent < 0 ? `${betrag(-k.restCent, w)} über Budget` : `Rest ${betrag(k.restCent, w)}`}
                          </span>
                        )}
                        {k.angeboteCent > 0 && <span>Angebote {betrag(k.angeboteCent, w)}</span>}
                        {hatKinder && k.planCent !== null && <span>verteilt {betrag(k.verteiltCent, w)}</span>}
                        <span>
                          {k.anzahlRechnungen} {k.anzahlRechnungen === 1 ? 'Rechnung' : 'Rechnungen'}
                          {k.anzahlAngebote ? `, ${k.anzahlAngebote} ${k.anzahlAngebote === 1 ? 'Angebot' : 'Angebote'}` : ''}
                        </span>
                      </div>
                    </li>
                  );
                })}
            </ul>
          )}
        </Karte>
      </div>

      <div className="stapel">
        <Rechner projektId={projektId} projektWaehrung={w} />
        <p className="leise klein bu-ohne-abstand">
          Alle Beträge in Euro und brutto. Bei Belegen in einer anderen Währung zeigt Überfahren mit der Maus (Handy: Antippen) den Originalbetrag.
        </p>
      </div>

      {einst && <EinstellungenDialog projektId={projektId} s={s} schliessen={() => setEinst(false)} fertig={neuLaden} />}
      {kat && <KategorieDialog projektId={projektId} s={s} k={kat.k} eltern={kat.eltern} darfLoeschen={darfLoeschen} schliessen={() => setKat(null)} fertig={neuLaden} />}
    </div>
  );
}

function versteckt(k: KategorieAnsicht, alle: KategorieAnsicht[], zu: Set<string>): boolean {
  let e = k.elternId;
  while (e) {
    if (zu.has(e)) return true;
    e = alle.find((x) => x.id === e)?.elternId ?? null;
  }
  return false;
}

function useZuklappen() {
  const [zu, setZu] = useState<Set<string>>(() => new Set());
  return {
    zu,
    umschalten: (id: string) =>
      setZu((alt) => {
        const n = new Set(alt);
        if (n.has(id)) n.delete(id);
        else n.add(id);
        return n;
      }),
  };
}

function Kennzahl({ titel, wert, hinweis, art }: { titel: string; wert: string; hinweis?: string; art?: 'gefahr' | 'ok' }) {
  return (
    <div className="bu-kennzahl">
      <span className="bu-kennzahl-titel">{titel}</span>
      <strong className={`bu-kennzahl-wert${art ? ` bu-text-${art}` : ''}`}>{wert}</strong>
      {hinweis && <span className="leise klein">{hinweis}</span>}
    </div>
  );
}

function EinstellungenDialog({ projektId, s, schliessen, fertig }: { projektId: string; s: BudgetStand; schliessen(): void; fertig(): void }) {
  const e = s.einstellungen;
  const [gesamt, setGesamt] = useState(e.gesamtCent === null ? '' : centAlsEingabe(e.gesamtCent));
  const [warnAb, setWarnAb] = useState(String(e.warnAbProzent));
  const a = useAktion();
  const gesamtCent = gesamt.trim() === '' ? null : centAusText(gesamt);
  const warnZahl = /^\d{1,3}$/.test(warnAb.trim()) ? Number(warnAb.trim()) : null;
  const ungueltig = (gesamt.trim() !== '' && (gesamtCent === null || gesamtCent < 0)) || warnZahl === null || warnZahl < 1 || warnZahl > 100;

  async function speichern() {
    const r = await a.ausfuehren(() => api.put(pfad(projektId, '/einstellungen'), { gesamtCent, warnAbProzent: warnZahl }));
    if (r) {
      fertig();
      schliessen();
    }
  }

  return (
    <Dialog
      titel="Budget-Einstellungen"
      offen
      schliessen={schliessen}
      fuss={
        <>
          <Knopf art="haupt" onClick={speichern} disabled={a.laeuft || ungueltig}>
            {a.laeuft ? 'Speichert …' : 'Speichern'}
          </Knopf>
          <Knopf onClick={schliessen}>Abbrechen</Knopf>
        </>
      }
    >
      <div className="stapel">
        <Meldung>{a.fehler}</Meldung>
        <Feld titel="Gesamtbudget (€)" hinweis="Leer lassen, wenn das Budget die Summe der Kategorien sein soll.">
          <Eingabe inputMode="decimal" value={gesamt} onChange={(x) => setGesamt(x.target.value)} placeholder="z. B. 25.000" aria-invalid={gesamt.trim() !== '' && gesamtCent === null} />
        </Feld>
        <Feld titel="Warnung „fast aufgebraucht“ ab (%)" hinweis="Ab diesem Anteil wird ein Budget gelb markiert. Standard: 90 %.">
          <Eingabe inputMode="numeric" value={warnAb} onChange={(x) => setWarnAb(x.target.value)} aria-invalid={warnZahl === null || warnZahl < 1 || warnZahl > 100} style={{ maxWidth: 120 }} />
        </Feld>
        <p className="leise klein bu-ohne-abstand">
          Die Projektwährung ist Euro. Belege in anderen Währungen werden mit dem EZB-Kurs vom Belegdatum umgerechnet; der Originalbetrag erscheint beim Überfahren mit der Maus.
        </p>
      </div>
    </Dialog>
  );
}

function KategorieDialog({
  projektId,
  s,
  k,
  eltern,
  darfLoeschen,
  schliessen,
  fertig,
}: {
  projektId: string;
  s: BudgetStand;
  k: KategorieAnsicht | null;
  eltern: string | null;
  darfLoeschen: boolean;
  schliessen(): void;
  fertig(): void;
}) {
  const [name, setName] = useState(k?.name ?? '');
  const [plan, setPlan] = useState(k?.planCent == null ? '' : centAlsEingabe(k.planCent));
  const [elternId, setElternId] = useState(eltern ?? '');
  const a = useAktion();
  const w = PROJEKT_WAEHRUNG;
  const planCent = plan.trim() === '' ? null : centAusText(plan);
  const ungueltig = !name.trim() || (plan.trim() !== '' && (planCent === null || planCent < 0));

  // Als Eltern nicht erlaubt: die Kategorie selbst und ihre Unterkategorien
  const gesperrt = new Set<string>();
  if (k) {
    gesperrt.add(k.id);
    let neu = true;
    while (neu) {
      neu = false;
      for (const x of s.kategorien) if (x.elternId && gesperrt.has(x.elternId) && !gesperrt.has(x.id)) (gesperrt.add(x.id), (neu = true));
    }
  }
  const elternName = s.kategorien.find((x) => x.id === elternId)?.pfad;

  async function speichern() {
    const body = { name: name.trim(), planCent, elternId: elternId || null };
    const r = await a.ausfuehren(() =>
      k ? api.patch(pfad(projektId, `/kategorien/${k.id}`), { ...body, version: k.version }) : api.post(pfad(projektId, '/kategorien'), body),
    );
    if (r) {
      fertig();
      schliessen();
    }
  }

  async function loeschen() {
    if (!k) return;
    const ziel = k.elternId ? `„${s.kategorien.find((x) => x.id === k.elternId)?.name}“` : '„Ohne Kategorie“';
    if (
      !confirm(
        `Kategorie „${k.name}“ löschen?\n\nSie liegt 180 Tage im Papierkorb und lässt sich dort zurückholen. Solange stehen ihre Unterkategorien und Belege unter ${ziel}.`,
      )
    )
      return;
    const r = await a.ausfuehren(() => api.delete(pfad(projektId, `/kategorien/${k.id}`)));
    if (r) {
      fertig();
      schliessen();
    }
  }

  return (
    <Dialog
      titel={k ? `Kategorie „${k.name}“` : elternName ? `Unterkategorie in „${elternName}“` : 'Neue Kategorie'}
      offen
      schliessen={schliessen}
      fuss={
        <>
          <Knopf art="haupt" onClick={speichern} disabled={a.laeuft || ungueltig}>
            {a.laeuft ? 'Speichert …' : 'Speichern'}
          </Knopf>
          <Knopf onClick={schliessen}>Abbrechen</Knopf>
          {k && darfLoeschen && (
            <>
              <span className="abstand" />
              <Knopf art="gefahr" onClick={loeschen} disabled={a.laeuft}>
                {Symbol.muell} Löschen
              </Knopf>
            </>
          )}
        </>
      }
    >
      <form
        className="stapel"
        onSubmit={(e) => {
          e.preventDefault();
          if (!ungueltig) void speichern();
        }}
      >
        <Meldung>{a.fehler}</Meldung>
        <Feld titel="Name">
          <Eingabe autoFocus value={name} onChange={(e) => setName(e.target.value)} maxLength={120} placeholder="z. B. Technik" />
        </Feld>
        <Feld titel={`Geplantes Budget (${w})`} hinweis="Leer lassen für eine Kategorie ohne eigenes Budget.">
          <Eingabe inputMode="decimal" value={plan} onChange={(e) => setPlan(e.target.value)} placeholder="z. B. 5.000" aria-invalid={plan.trim() !== '' && planCent === null} />
        </Feld>
        <Feld titel="Liegt unter">
          <Auswahl value={elternId} onChange={(e) => setElternId(e.target.value)}>
            <option value="">– ganz oben –</option>
            {s.kategorien
              .filter((x) => !gesperrt.has(x.id))
              .map((x) => (
                <option key={x.id} value={x.id}>
                  {'  '.repeat(x.tiefe)}
                  {x.name}
                </option>
              ))}
          </Auswahl>
        </Feld>
        <button type="submit" hidden />
      </form>
    </Dialog>
  );
}
